import { helpFor } from "../metric-help.js";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Breadcrumb } from "../shell/Shell.js";
import { humanizeReason } from "../format.js";
import { useApi } from "../api/useApi.js";
import type { ProvenanceSummary } from "../api/client.js";
import {
  RagDot,
  Spine,
  spineLevels,
  ConfidencePill,
  Basis,
  basisKindOf,
  ProvenancePopover,
  EmptyState,
  HonestState,
  MetricTile,
  Drawer,
  type Rag,
  HelpTip,
} from "../components.js";
import { DimensionEvidenceContent } from "./EvidenceContent.js";
import { CapabilityPanel, FrictionPanel, ReliabilityPanel } from "./ProjectAnalyticsPanels.js";
import { DataHealthPanel } from "./DataHealthPanel.js";
import { AsOfControl, AsOfProjectView, useAsOfRequest } from "./AsOfView.js";
import { EstimationPanel } from "./EstimationPanel.js";
import { NarrativePanel } from "./NarrativePanel.js";
import { WhatIfSimulatorPanel } from "./WhatIfSimulatorPanel.js";
import { RollupTree, BlendedDoraPanel } from "./ProgramRollup.js";
import type {
  DoraDerivation,
  FrictionDerivation,
  Metric,
  ModuleCapabilityValue,
  ReliabilityDerivation,
} from "./projectMetrics.js";
import "./project.css";

/**
 * Project detail — built to the locked demo (project.html). M0 is faithful in structure, honest in
 * data: the header status + confidence and the per-dimension EVIDENCE COVERAGE (change recency / CI /
 * deploy) are the real E5.0 figures; a dimension the source can't observe shows the honest "N/A"
 * state (never scored zero). The richer analytics the demo shows (forecast, DORA, capability,
 * reliability, friction, AI narrative) are not yet computed in M0, so they render as calm honest
 * states rather than faked numbers. Every figure drills to its provenance.
 */

interface Dimension {
  dimension: string;
  level: string;
  lineage: number[];
  confidence: number | null;
}
type DeliveryMode = "scope-driven" | "date-driven" | "continuous";
type ConfidenceBasis = "estimation" | "throughput" | "hybrid";
interface ProjectProfile {
  workflowProfile: string | null;
  stageModel: string | null;
  confidenceBasis: ConfidenceBasis | null;
  deliveryMode: DeliveryMode | null;
  targetDate: string | null;
}
interface ThroughputForecastValue {
  modelVersion: string;
  remainingItems: number;
  activePeriods: number;
  trials: number;
  seed: number;
  p50: number;
  p85Raw: number;
  p85Adjusted: number;
  p95: number;
  bandMultiplier: number;
}
interface DurationSummary {
  n: number;
  meanSeconds: number;
  p50Seconds: number;
  p85Seconds: number;
  minSeconds: number;
  maxSeconds: number;
}
interface ThroughputValue {
  count: number;
  windowSeconds: number;
  perWeek: number;
}
interface WipValue {
  count: number;
  items: string[];
}
interface FlowEfficiencyValue {
  ratio: number;
  n: number;
}
interface AgingValue {
  oldestSeconds: number;
  meanSeconds: number;
  n: number;
}
interface FlowMetrics {
  leadTime: Metric<DurationSummary>;
  cycleTime: Metric<DurationSummary>;
  throughput: Metric<ThroughputValue>;
  wip: Metric<WipValue>;
  flowEfficiency: Metric<FlowEfficiencyValue>;
  aging: Metric<AgingValue>;
  effectiveAt: string;
}
interface EstimationCapabilityValue {
  estimateCount: number;
  workItemsEstimated: number;
  kinds: string[];
}
interface ProjectView {
  id: string;
  name: string;
  scopeType: string;
  parentProjectId: string | null;
  status: string;
  confidence: number | null;
  capped: boolean;
  reason: string;
  provenance: ProvenanceSummary | null;
  dimensions?: Dimension[];
  profile: ProjectProfile;
  forecast: Metric<ThroughputForecastValue>;
  flowMetrics: FlowMetrics;
  estimate: Metric<EstimationCapabilityValue>;
  // E7.4 module gate + the E5.6/E5.7 derivations — returned by GET /projects/:id since NX-2359,
  // rendered by the E8.7 panels (NX-2632).
  capability: Metric<ModuleCapabilityValue>;
  reliability: ReliabilityDerivation;
  friction: FrictionDerivation;
  // E5.2 — returned by GET /projects/:id since NX-2616 (portfolio.ts's toView), rendered per
  // sub-project by RollupTree and blended across sub-projects by BlendedDoraPanel (NX-2631/E8.4).
  dora: DoraDerivation;
}
interface ProjectResponse {
  project: ProjectView;
  subProjects?: ProjectView[];
}

interface ProjectWorkItem {
  id: string;
  code: string;
  title?: string | null;
  status: string | null;
  parentEpicCode: string | null;
  storyPoints?: number | null;
}

function ProjectWorkList({ projectId }: { projectId: string }): React.ReactNode {
  const state = useApi<ProjectWorkItem[]>(`/projects/${projectId}/work-items`);
  if (state.status === "loading") return null;
  if (state.status === "error") {
    return (
      <p className="meta" role="alert">
        Couldn't load the project work list: {state.error.message}
      </p>
    );
  }

  const items = state.envelope.data;
  const showPoints = items.length > 0 && items.every((item) => Object.hasOwn(item, "storyPoints"));
  return (
    <section className="panel pad project-work-panel" aria-label="Project work items">
      <h3 className="project-work-heading">Work items</h3>
      {items.length === 0 ? (
        <p className="empty">No work items in this project.</p>
      ) : (
        <div className="project-work-scroll" role="region" aria-label="Work item list" tabIndex={0}>
          <table className="project-work-table">
            <thead>
              <tr>
                <th scope="col">Key</th>
                <th scope="col">Title</th>
                <th scope="col">Status</th>
                {showPoints && <th scope="col">Story points</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td className="project-work-code">{item.code}</td>
                  <td>{item.title ?? "N/A"}</td>
                  <td className="project-work-status">{item.status ?? "N/A"}</td>
                  {showPoints && (
                    <td className="project-work-points">{item.storyPoints ?? null}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const STATUS_RAG: Record<string, Rag> = {
  green: "green",
  yellow: "amber",
  red: "red",
  unsupported: "neutral",
};
const STATUS_LABEL: Record<string, string> = {
  green: "On track",
  yellow: "At risk",
  red: "Off track",
  unsupported: "Unsupported",
};
const LEVEL_RAG: Record<string, Rag> = { ok: "green", warn: "amber", bad: "red", "n/a": "neutral" };
const LEVEL_LABEL: Record<string, string> = {
  ok: "Healthy",
  warn: "Needs attention",
  bad: "Failing",
};
const DIM_LABEL: Record<string, string> = {
  change_recency: "Change recency",
  ci: "CI outcomes",
  deploy: "Deploy outcomes",
};
const ragOf = (status: string): Rag => STATUS_RAG[status] ?? "neutral";
const labelOf = (status: string): string => STATUS_LABEL[status] ?? status;
const DAY_SECONDS = 86_400;
const TARGET_DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
const pretty = (value: string | null): string =>
  value === null ? "N/A" : value.replaceAll("-", " ");
const decimal = (value: number, places = 1): string => {
  const rounded = Number(value.toFixed(places));
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};
const metricConfidence = <T,>(metric: Metric<T>): number | null =>
  metric.kind === "value" && metric.confidence.kind === "value"
    ? metric.confidence.confidence
    : null;
const metricReason = <T,>(metric: Metric<T>, fallback: string): string =>
  metric.kind === "n/a" ? metric.reason : fallback;

/**
 * One dimension of evidence coverage: its level + confidence, with a drill to the SOURCE EVENTS
 * behind it (E7.3 lineage). No naked number — the figure exposes how many source events it was
 * computed from and its provenance.
 */
function DimensionTile({
  dim,
  provenance,
  onOpenEvidence,
}: {
  dim: Dimension;
  provenance: ProvenanceSummary | null;
  onOpenEvidence: () => void;
}): React.ReactNode {
  const label = DIM_LABEL[dim.dimension] ?? dim.dimension;
  return (
    <div
      className="metric"
      role="button"
      tabIndex={0}
      aria-label={`Open ${label} evidence drawer`}
      onClick={onOpenEvidence}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpenEvidence();
        }
      }}
    >
      <div className="lab">{label}</div>
      <div
        className="v"
        style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 18 }}
      >
        <RagDot
          status={LEVEL_RAG[dim.level] ?? "neutral"}
          label={LEVEL_LABEL[dim.level] ?? dim.level}
        />
        {LEVEL_LABEL[dim.level] ?? dim.level}
      </div>
      <div className="mc">
        <ConfidencePill value={dim.confidence} />{" "}
        <ProvenancePopover
          source={provenance?.source ?? "—"}
          method={provenance?.method ?? "evidence-derived"}
          confidence={dim.confidence}
        >
          {dim.lineage.length} source event{dim.lineage.length === 1 ? "" : "s"} →
        </ProvenancePopover>
      </div>
      {helpFor(label) !== undefined && <HelpTip text={helpFor(label)!} label={label} />}
      <span className="clickhint" aria-hidden="true">
        DRILL IN
      </span>
    </div>
  );
}

export function Project(): React.ReactNode {
  const { id = "" } = useParams();
  const state = useApi<ProjectResponse>(`/projects/${id}`);
  const crumb = (
    <Breadcrumb items={[{ label: "Portfolio", to: "/portfolio" }, { label: "Project" }]} />
  );
  // NX-2744 (E8.13) — ADR-018 time-travel. When an as-of/snapshot view is active the LIVE body
  // never mounts: the past view replaces it wholesale, so no live panel can masquerade as history.
  const asOf = useAsOfRequest();
  if (asOf.active) {
    return (
      <>
        {crumb}
        <AsOfControl />
        <AsOfProjectView
          projectId={id}
          query={asOf.query}
          asOfParam={asOf.asOfParam}
          snapshotParam={asOf.snapshotParam}
        />
      </>
    );
  }
  return (
    <>
      {state.status === "loading" && (
        <>
          {crumb}
          <div className="meta">Loading the project from the evidence store…</div>
        </>
      )}
      {state.status === "error" && (
        <>
          {crumb}
          <EmptyState
            message={
              state.error.status === 404
                ? "Project not found."
                : `Couldn't load the project: ${state.error.message}`
            }
          />
        </>
      )}
      {state.status === "ready" && (
        <>
          <AsOfControl />
          <ProjectBody data={state.envelope.data} />
        </>
      )}
    </>
  );
}

export function ProjectBody({ data }: { data: ProjectResponse }): React.ReactNode {
  const p = data.project;
  const dims = p.dimensions ?? [];
  const [openDimension, setOpenDimension] = useState<Dimension | null>(null);
  const profileMissing = p.profile.deliveryMode === null || p.profile.confidenceBasis === null;
  return (
    <>
      <Breadcrumb items={[{ label: "Portfolio", to: "/portfolio" }, { label: p.name }]} />
      <div className="head">
        <h1 id="title">
          <Spine levels={spineLevels(ragOf(p.status))} label={labelOf(p.status)} /> {p.name}
        </h1>
      </div>
      <div className="meta project-meta">
        {humanizeReason(p.reason)} · <ConfidencePill value={p.confidence} /> ·{" "}
        <ProvenancePopover
          source={p.provenance?.source ?? "—"}
          method={p.provenance?.method ?? "evidence-derived status"}
          confidence={p.provenance?.confidence ?? p.confidence}
        >
          provenance
        </ProvenancePopover>
      </div>
      <div className="meta project-meta">
        Delivery mode: <b>{pretty(p.profile.deliveryMode)}</b> · Confidence basis:{" "}
        <b>{pretty(p.profile.confidenceBasis)}</b> ·{" "}
        <Basis kind={basisKindOf(p.profile.confidenceBasis)} /> ·{" "}
        <span className="project-target">
          {!p.profile.targetDate ? (
            "No target declared"
          ) : (
            <>
              Declared target: <b>{TARGET_DATE_FMT.format(new Date(p.profile.targetDate))}</b>
            </>
          )}
        </span>
      </div>
      {profileMissing && (
        <HonestState
          kind="notobs"
          detail="Project profile is only partially declared (delivery mode and/or confidence basis are N/A), so delivery analytics stay conservative."
        />
      )}

      <div className="sectitle">
        <h3 className="sec">Evidence coverage</h3>
        <span className="cnt">the observed signals behind this status (E5.0)</span>
      </div>
      {dims.length === 0 ? (
        <HonestState
          kind="notobs"
          detail="Per-dimension evidence is shown to delivery-side roles. Your role sees the rolled-up status and confidence above, each drillable to its source."
        />
      ) : (
        <div className="grid cols-3 metrics click">
          {dims.map((d) =>
            d.level === "n/a" ? (
              <HonestState
                key={d.dimension}
                kind="notobs"
                title={DIM_LABEL[d.dimension] ?? d.dimension}
                detail="This source can't report it — N/A, never scored as zero."
              />
            ) : (
              <DimensionTile
                key={d.dimension}
                dim={d}
                provenance={p.provenance}
                onOpenEvidence={() => setOpenDimension(d)}
              />
            ),
          )}
        </div>
      )}
      <Drawer
        open={openDimension !== null}
        title={
          openDimension === null
            ? ""
            : `${DIM_LABEL[openDimension.dimension] ?? openDimension.dimension} evidence`
        }
        subtitle={
          openDimension === null ? undefined : (
            <>
              <b>{p.name}</b> · drill-to-proof trail
            </>
          )
        }
        onClose={() => setOpenDimension(null)}
      >
        {/* NX-2797: drill into the dimension's own lineage, not the project id as a work item. */}
        {openDimension !== null && (
          <DimensionEvidenceContent
            projectId={p.id}
            dimension={openDimension.dimension}
            dimensionLabel={DIM_LABEL[openDimension.dimension] ?? openDimension.dimension}
          />
        )}
      </Drawer>

      {/* E8.4 (NX-2631) — the program-rollup body. `subProjects !== undefined` (not
          `.length > 0`) is the correct gate: the API only sets this key at all when
          `scopeType === "program"` (portfolio.ts), so a defined-but-empty array IS a program with
          zero children, and must render its own honest empty state (TC2) rather than the whole
          section silently disappearing, which is what the previous `.length > 0` gate did. */}
      {data.subProjects !== undefined && (
        <>
          <div className="sectitle">
            <h3 className="sec">Sub-projects</h3>
            <span className="cnt">
              program rollup — each sub-project's own status and confidence
            </span>
          </div>
          <RollupTree subProjects={data.subProjects} />
          <BlendedDoraPanel subProjects={data.subProjects} />
        </>
      )}

      <div className="sectitle">
        <h3 className="sec">Delivery analytics</h3>
        <span className="cnt">forecast · flow metrics · estimate profile</span>
      </div>
      <div className="grid cols-2">
        <div className="panel pad">
          <h3 className="sec">Forecast</h3>
          {p.forecast.kind === "value" ? (
            <>
              <div className="grid cols-2">
                <MetricTile
                  label="P50 completion"
                  value={
                    <>
                      {decimal(p.forecast.value.p50)} <small>periods</small>
                    </>
                  }
                  confidence={metricConfidence(p.forecast)}
                />
                <MetricTile
                  label="P85 completion"
                  value={
                    <>
                      {decimal(p.forecast.value.p85Adjusted)} <small>periods</small>
                    </>
                  }
                  confidence={metricConfidence(p.forecast)}
                />
              </div>
              <div className="meta">
                Remaining: <b>{p.forecast.value.remainingItems}</b> items · active periods:{" "}
                <b>{p.forecast.value.activePeriods}</b> · model{" "}
                <b>{p.forecast.value.modelVersion}</b>
              </div>
            </>
          ) : (
            <HonestState kind="nodata" title="Forecast" detail={metricReason(p.forecast, "N/A")} />
          )}
        </div>
        <div className="panel pad">
          <h3 className="sec">Estimation capability</h3>
          {p.estimate.kind === "value" ? (
            <div className="grid cols-2">
              <MetricTile
                label="Captured estimates"
                value={p.estimate.value.estimateCount}
                confidence={metricConfidence(p.estimate)}
              />
              <MetricTile
                label="Estimated work items"
                value={p.estimate.value.workItemsEstimated}
                confidence={metricConfidence(p.estimate)}
              />
              <MetricTile
                label="Estimate kinds"
                value={p.estimate.value.kinds.join(", ") || "N/A"}
                confidence={metricConfidence(p.estimate)}
              />
            </div>
          ) : (
            <HonestState
              kind="notobs"
              title="Estimate profile"
              detail={metricReason(p.estimate, "N/A")}
            />
          )}
        </div>
      </div>
      <ProjectWorkList projectId={p.id} />
      <div className="sectitle">
        <h3 className="sec">Flow metrics</h3>
        <span className="cnt">
          lead time · cycle time · throughput · WIP · flow efficiency · aging
        </span>
      </div>
      <div className="grid cols-3">
        {p.flowMetrics.leadTime.kind === "value" ? (
          <MetricTile
            label="Lead time p85"
            value={
              <>
                {decimal(p.flowMetrics.leadTime.value.p85Seconds / DAY_SECONDS)} <small>days</small>
              </>
            }
            confidence={metricConfidence(p.flowMetrics.leadTime)}
          />
        ) : (
          <HonestState
            kind="nodata"
            title="Lead time p85"
            detail={metricReason(p.flowMetrics.leadTime, "N/A")}
          />
        )}
        {p.flowMetrics.cycleTime.kind === "value" ? (
          <MetricTile
            label="Cycle time p85"
            value={
              <>
                {decimal(p.flowMetrics.cycleTime.value.p85Seconds / DAY_SECONDS)}{" "}
                <small>days</small>
              </>
            }
            confidence={metricConfidence(p.flowMetrics.cycleTime)}
          />
        ) : (
          <HonestState
            kind="nodata"
            title="Cycle time p85"
            detail={metricReason(p.flowMetrics.cycleTime, "N/A")}
          />
        )}
        {p.flowMetrics.throughput.kind === "value" ? (
          <MetricTile
            label="Throughput"
            value={
              <>
                {decimal(p.flowMetrics.throughput.value.perWeek, 2)} <small>items/week</small>
              </>
            }
            confidence={metricConfidence(p.flowMetrics.throughput)}
          />
        ) : (
          <HonestState
            kind="nodata"
            title="Throughput"
            detail={metricReason(p.flowMetrics.throughput, "N/A")}
          />
        )}
        {p.flowMetrics.wip.kind === "value" ? (
          <MetricTile
            label="WIP"
            value={p.flowMetrics.wip.value.count}
            confidence={metricConfidence(p.flowMetrics.wip)}
          />
        ) : (
          <HonestState kind="nodata" title="WIP" detail={metricReason(p.flowMetrics.wip, "N/A")} />
        )}
        {p.flowMetrics.flowEfficiency.kind === "value" ? (
          <MetricTile
            label="Flow efficiency"
            signal={p.flowMetrics.flowEfficiency.value.ratio * 100}
            value={
              <>
                {decimal(p.flowMetrics.flowEfficiency.value.ratio * 100)} <small>%</small>
              </>
            }
            confidence={metricConfidence(p.flowMetrics.flowEfficiency)}
          />
        ) : (
          <HonestState
            kind="nodata"
            title="Flow efficiency"
            detail={metricReason(p.flowMetrics.flowEfficiency, "N/A")}
          />
        )}
        {p.flowMetrics.aging.kind === "value" ? (
          <MetricTile
            label="Oldest in-flight"
            value={
              <>
                {decimal(p.flowMetrics.aging.value.oldestSeconds / DAY_SECONDS)} <small>days</small>
              </>
            }
            confidence={metricConfidence(p.flowMetrics.aging)}
          />
        ) : (
          <HonestState
            kind="nodata"
            title="Oldest in-flight"
            detail={metricReason(p.flowMetrics.aging, "N/A")}
          />
        )}
      </div>
      {/* NX-2635 (E8.9) — the Narrative panel + what-changed digest, self-fetching (see
          NarrativePanel.tsx's own doc comment for why it is a sibling file, same reasoning
          EstimationPanel.tsx already used). Placed FIRST among the sub-panels: the locked mockup
          (design/ui/mockup-web.html) puts `<h2>Narrative</h2>` immediately after the hero metrics
          and before Reliability/Components/Capability/DevEx-friction — a prose summary that reads
          before the detailed panels it summarizes, not after them. */}
      <NarrativePanel projectId={p.id} />
      {/* E8.7 (NX-2632): capability / reliability / friction. Props only — the envelope is already
          fetched above, so no panel does its own request. The locked design places these here as
          project-detail sub-panels, which is why NX-2645 retired the top-level nav destinations
          that had been invented for them. */}
      <ReliabilityPanel reliability={p.reliability} />
      <FrictionPanel friction={p.friction} />
      <CapabilityPanel capability={p.capability} />
      {/* NX-2738 (E12.6) — tenant-facing freshness/coverage: which sources feed this project, what
          each has observed vs not yet, and when data last landed. Self-fetching (role-projected). */}
      <DataHealthPanel projectId={p.id} />
      {/* NX-2634 (E13.8) — a single-responsibility, self-fetching sibling (see EstimationPanel.tsx's
          own doc comment for why it fetches independently rather than taking props like the three
          panels above). */}
      <EstimationPanel projectId={p.id} />
      {/* NX-2633 (E8.8) — the improvement (what-if) simulator: a Project-detail sub-panel per
          design/08 §1 IA + §2 flow 2, not a top-level nav destination. `baselineForecast` reuses
          the ALREADY-fetched `p.forecast` (never a second request for the read-only path — AC7). */}
      <WhatIfSimulatorPanel
        projectId={p.id}
        deliveryMode={p.profile.deliveryMode}
        targetDate={p.profile.targetDate}
        baselineForecast={p.forecast}
      />
    </>
  );
}
